import {
	faPlus,
	faSpinnerThird,
	faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UturnUserRequest } from '@uturn/api/v1';
import {
	useFetchUturnUserByIdSuspense,
	useUpdateUserDetails,
} from '@uturn/api/v1';
import { MetaDataContext } from '@uturn/portal/context';
import { queryClient } from '@uturn/portal/query';
import {
	Button,
	cn,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormFieldReadOnly,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Separator,
	Switch,
	sonner,
} from '@uturn/ui-kit';
import { useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formNotificationsSchema, type TForm } from '../schema';

export function AccountNotifications() {
	const { t } = useTranslation();
	const { metadata } = useContext(MetaDataContext);

	const userId = metadata?.user?.id ?? 0;

	const { data: accountSettings, queryKey } = useFetchUturnUserByIdSuspense(
		userId,
		{
			query: {
				refetchOnWindowFocus: false,
				select: (data) => data.data,
			},
		},
	);

	const {
		email: defaultEmail,
		notificationEmails,
		...accountSettingsRest
	} = accountSettings;

	const initialNotificationEmails = notificationEmails
		.filter((item) => item !== defaultEmail)
		.map((emailValue) => ({ email: emailValue }));

	/**
	 * Main email is rendered read-only and is not part of the form state.
	 * On submit it's prepended to the additional emails before sending.
	 */
	const [email] = useState(defaultEmail);

	const form = useForm<TForm>({
		resolver: zodResolver(formNotificationsSchema),
		mode: 'onBlur',
		defaultValues: {
			...accountSettingsRest,
			notificationEmails: initialNotificationEmails,
		},
	});

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: 'notificationEmails',
	});

	const watchNotifications = form.watch('notifications');

	const { mutate, isPending } = useUpdateUserDetails();

	const onSubmit = (formData: TForm) => {
		const additionalEmails = formData.notificationEmails
			.map((entry) => entry.email)
			.filter((entry) => entry !== '');

		const data: UturnUserRequest = {
			...formData,
			notificationEmails: [email, ...additionalEmails],
		};

		mutate(
			{
				id: userId,
				data,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey });
					// Drop empty rows after a successful save, mirroring org/profile.
					form.setValue(
						'notificationEmails',
						form
							.getValues('notificationEmails')
							.filter((entry) => entry.email !== ''),
					);
					sonner.success(
						t(
							'pages.user.settings.notifications.save.success',
							'User has been updated!',
						),
					);
				},
				onError: () => {
					sonner.error(t('general.try-again-later', 'Please try again later.'));
				},
			},
		);
	};

	const handleFormSubmit = form.handleSubmit(onSubmit);

	const handleNotificationUpdate = (index: number, enabled: boolean) => {
		form.setValue(`notifications.${index}.enabled`, enabled);
		handleFormSubmit();
	};

	return (
		<>
			<Helmet
				title={t(
					'pages.user.settings.notifications.title',
					'Account - Notifications',
				).toString()}
			/>
			<div className="space-y-6">
				<div>
					<h3 className="text-lg font-medium">
						{t('pages.user.settings.notifications.title', 'Notifications')}
					</h3>
					<p className="text-muted-foreground text-sm">
						{t(
							'pages.user.settings.notifications.description',
							'Configure how you receive notifications.',
						)}
					</p>
				</div>
				<Separator />
				<Form {...form}>
					<form onSubmit={handleFormSubmit} className="space-y-8">
						<div>
							<FormLabel>
								{t(
									'pages.user.settings.notifications.emails.title',
									'Notification emails',
								)}
							</FormLabel>
							<FormDescription>
								{t(
									'pages.user.settings.notifications.emails.description',
									'Add emails on which you want to receive notifications.',
								)}
							</FormDescription>
							<FormFieldReadOnly className="mt-2" value={email} />
							{fields.map((fieldItem, index) => (
								<FormField
									key={fieldItem.id}
									control={form.control}
									name={`notificationEmails.${index}.email`}
									render={({ field }) => (
										<FormItem className="mt-2">
											<FormControl>
												<div className="flex space-x-2">
													<Input {...field} type="email" />

													<Button
														type="button"
														variant="destructive"
														size="icon"
														onClick={() => remove(index)}
													>
														<Icon icon={faTrashCan} />
													</Button>
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							))}
							<Button
								type="button"
								className="mt-2 flex gap-2 p-0"
								variant="link"
								onClick={() => append({ email: '' })}
							>
								<Icon icon={faPlus} />
								<span>
									{t(
										'pages.user.settings.notifications.emails.add_email',
										'Add email',
									)}
								</span>
							</Button>
						</div>
						<Button
							type="button"
							disabled={isPending || !form.formState.isValid}
							onClick={handleFormSubmit}
						>
							{isPending && (
								<Icon spin className="mr-3" icon={faSpinnerThird} />
							)}
							{t(
								'pages.user.settings.notifications.actions.save',
								'Update notification emails',
							)}
						</Button>
						<div className="pt-4">
							{watchNotifications?.map((notificationOption, index) => (
								<FormField
									key={notificationOption.code}
									control={form.control}
									name={`notifications.${index}`}
									render={() => (
										<FormItem>
											<FormLabel className={cn(index !== 0 && 'sr-only')}>
												{t(
													'pages.user.settings.notifications.title',
													'Notifications',
												)}
											</FormLabel>
											<FormDescription className={cn(index !== 0 && 'sr-only')}>
												{t(
													'pages.user.settings.notifications.description',
													'Configure which notifications you want to receive.',
												)}
											</FormDescription>
											<FormControl>
												<div className="flex flex-row items-center justify-between rounded-lg border p-4">
													<div className="space-y-0.5">
														<FormLabel>
															{t(
																`pages.user.settings.notifications.options.${notificationOption.code}.title`,
																`${notificationOption.code}`,
															)}
														</FormLabel>
														<FormDescription>
															{t(
																`pages.user.settings.notifications.options.${notificationOption.code}.description`,
																`	Receive emails for friend requests, follows, and
															more.`,
															)}
														</FormDescription>
													</div>
													<Switch
														checked={notificationOption.enabled}
														onCheckedChange={(checked) =>
															handleNotificationUpdate(index, checked)
														}
													/>
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							))}
						</div>
					</form>
				</Form>
			</div>
		</>
	);
}
